/**
 * admin/src/components/layout/AdminLayout.tsx
 * Shell layout v3.0: Topbar + Collapsible Sidebar + Main content
 * Features: Light/Dark theme toggle, sidebar collapse to icon-only
 */

import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../hooks/useAdmin';
import { useTheme } from '../../hooks/useTheme';
import { useSidebar } from '../../hooks/useSidebar';

// ── Nav groups ──────────────────────────────────────────────────────────────────
const NAV_GROUPS = [
  {
    label: 'Tổng quan',
    items: [
      { to: '/admin',      icon: '◈',  label: 'Dashboard',  end: true },
      { to: '/admin/analytics', icon: '▲', label: 'Analytics' },
    ],
  },
  {
    label: 'Quản lý',
    items: [
      { to: '/admin/users',     icon: '👤', label: 'Users' },
      { to: '/admin/students',  icon: '🎓', label: 'Học sinh' },
      { to: '/admin/subscriptions', icon: '💳', label: 'Gói đăng ký' },
    ],
  },
  {
    label: 'Nội dung',
    items: [
      { to: '/admin/content',  icon: '📚', label: 'Bài học' },
      { to: '/admin/blog',     icon: '✍️', label: 'Blog' },
    ],
  },
  {
    label: 'Giao tiếp',
    items: [
      { to: '/admin/notifications',    icon: '📧', label: 'Email hàng loạt' },
      { to: '/admin/email-templates',  icon: '📨', label: 'Email Templates' },
      { to: '/admin/feedback',         icon: '💬', label: 'Feedback & Lỗi' },
    ],
  },
  {
    label: 'Hệ thống',
    items: [
      { to: '/admin/settings', icon: '⚙️', label: 'Settings' },
      { to: '/admin/backup',   icon: '🗄️', label: 'Backup' },
    ],
  },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

// ── SVG Icons ───────────────────────────────────────────────────────────────────
function SunIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18l6-6-6-6"/>
    </svg>
  );
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const { user, logout } = useAdminAuth();
  const { toggleTheme, isDark } = useTheme();
  const { collapsed, toggleSidebar } = useSidebar();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="admin-shell">
      {/* ── Topbar ────────────────────────────────────────────────── */}
      <header className="admin-topbar">
        <a href="/admin" className="admin-topbar__logo">
          <span className="admin-topbar__logo-dot" />
          {!collapsed && <span>luyenthi.io.vn</span>}
        </a>
        {!collapsed && (
          <span style={{
            fontSize: 11,
            color: 'var(--text-3)',
            paddingLeft: 8,
            borderLeft: '1px solid var(--border)',
            marginLeft: 4,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}>
            Admin v3
          </span>
        )}

        <div className="admin-topbar__spacer" />

        <div className="admin-topbar__user">
          {/* Theme Toggle */}
          <button
            className="btn btn-ghost btn-sm"
            onClick={toggleTheme}
            title={isDark ? 'Chuyển sang Light mode' : 'Chuyển sang Dark mode'}
            style={{
              padding: '6px 8px',
              gap: 0,
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}
            aria-label="Toggle theme"
          >
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>

          <span className="admin-topbar__badge">ADMIN</span>
          <span className="admin-topbar__email">{user?.email}</span>
          <button className="btn btn-ghost btn-sm" onClick={handleLogout}>
            Đăng xuất
          </button>
        </div>
      </header>

      {/* ── Sidebar ───────────────────────────────────────────────── */}
      <aside className="admin-sidebar">
        <nav className="sidebar-nav" style={{ flex: 1 }}>
          {NAV_GROUPS.map((group) => (
            <div key={group.label}>
              <div className="sidebar-section-label">{group.label}</div>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={'end' in item ? item.end : false}
                  data-label={item.label}
                  className={({ isActive }) =>
                    `sidebar-link ${isActive ? 'active' : ''}`
                  }
                  title={collapsed ? item.label : undefined}
                >
                  <span className="icon">{item.icon}</span>
                  <span className="sidebar-label">{item.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {/* Sidebar Footer */}
        <div className="sidebar-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          {!collapsed && (
            <div className="sidebar-version">
              <span style={{ color: 'var(--accent)', marginRight: 4 }}>◈</span>
              v3.0 · 2026
            </div>
          )}
          <button
            className="sidebar-collapse-btn"
            onClick={toggleSidebar}
            title={collapsed ? 'Mở rộng sidebar' : 'Thu gọn sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>
      </aside>

      {/* ── Main content ──────────────────────────────────────────── */}
      <main className="admin-main">{children}</main>
    </div>
  );
}
